import * as configData from "../../../config.json";

import { ArrowDownIcon, ArrowUpIcon } from "@vinomofo/icons";
import { Box, Button, Heading, Text } from "@vinomofo/components";
import React, { useEffect, useMemo, useState } from "react";
import { rewardIconsV2, tierColors } from "../account/rewards/Rewards";
import useLoyaltyContext, { useLoyalty } from "../../hooks/account/use-loyalty";

import { COLORS } from "../../styles";
import { CartActions } from "./CartActions";
import { CartItemRow } from "./CartItemRow";
import { CartPromotions } from "./CartPromotions";
import { CartShipping } from "../summary/CartShipping";
import { CartStoreCredit } from "../summary/CartStoreCredit";
import { CartSubtotal } from "../summary/CartSubtotal";
import { CartTotal } from "../summary/CartTotal";
import { OneClickCheckout } from "../one-click-checkout";
import {
  LoyaltyTierLowerCase,
  TierBanner,
  TierPoints,
  TierToAttain,
} from "../../contexts/loyalty/types";
import { VinoUser } from "../../contexts/auth/state";
import { determineTierIndex } from "../../utils/loyalty";
import { formatNumber } from "../../utils/string-helpers";
import goldBanner from "../../images/loyalty/gold-banner.svg";
import greenBanner from "../../images/loyalty/green-banner.svg";
import isBrowser from "../../utils/is-browser";
import { loyaltyTierNames } from "../../utils/constants";
import platinumBanner from "../../images/loyalty/platinum-banner.svg";
import silverBanner from "../../images/loyalty/silver-banner.svg";
import styled from "styled-components";
import useAuth from "../../hooks/use-auth";
import { useCart } from "../../hooks/use-cart";
import { useCredits } from "../../hooks/account/use-credits";
import { useStaffDiscount } from "../../hooks/cart/use-staff-discount";

export interface CartDropdownContentProps {
  onCloseRequested?: () => void;
}

const StyledWrapper = styled(Box)`
  @media screen and (max-height: 600px) {
    overflow: scroll;
    height: 320px;
  }
`;

const StyledItemsWrapper = styled(Box)`
  @media screen and (max-height: 600px) {
    max-height: 100% !important;
  }
`;

const StyledHeading = styled(Heading)<{ color: string }>`
  display: inline-flex;
  margin: 0px;
  -webkit-font-smoothing: antialiased;
  letter-spacing: -0.025em;
  line-height: 1;
  color: ${({ color }) => (color ? color : `black`)};
  font-family: "GT Walsheim", sans-serif;
  font-size: 24px;
`;

const StyledTextPoints = styled(Text)<{ color: string }>`
  font-size: 16px;
  color: ${({ color }) => (color ? color : `black`)};
  lineheight: 1.7;
  fontweight: 400;
  fontfamily: "Graphik", sans-serif;
  margin-top: 2px;
  margin-right: 24px;
`;

const StyledButton = styled(Button)`
  position: absolute;
  top: 17px;
  right: 14px;
  height: 24px;
`;

const Arrow = ({ style }: { style: any }) => {
  return (
    <svg
      style={style}
      width={style.width}
      height={style.height}
      viewBox={`0 0 268 110`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M63.9717 110H42L97.0283 55L42 0H63.9717L119 55L63.9717 110Z"
        fill={style.color}
        fillOpacity="0.25"
      />
      <path
        d="M32.9717 110H11L66.0283 55L11 0H32.9717L88 55L32.9717 110Z"
        fill={style.color}
        fillOpacity="0.25"
      />
      <path
        d="M93.9717 110H72L127.028 55L72 0H93.9717L149 55L93.9717 110Z"
        fill={style.color}
        fillOpacity="0.25"
      />
      <path
        d="M123.972 110H102L157.028 55L102 0H123.972L179 55L123.972 110Z"
        fill={style.color}
        fillOpacity="0.25"
      />
      <path
        d="M152.972 110H131L186.028 55L131 0H152.972L208 55L152.972 110Z"
        fill={style.color}
        fillOpacity="0.25"
      />
      <path
        d="M181.972 110H160L215.028 55L160 0H181.972L237 55L181.972 110Z"
        fill={style.color}
        fillOpacity="0.25"
      />
      <path
        d="M212.972 110H191L246.028 55L191 0H212.972L268 55L212.972 110Z"
        fill={style.color}
        fillOpacity="0.25"
      />
      <path
        d="M55.5 55.0029L-0.00296883 110L-0.00296402 -2.42611e-06L55.5 55.0029Z"
        fill={style.color}
        fillOpacity="0.25"
      />
    </svg>
  );
};

export const CartDropdownContent = ({
  onCloseRequested,
}: CartDropdownContentProps): JSX.Element => {
  const { cart, isCartOpen } = useCart();
  const { cartItemsRegularPrice, fetchTotalStaffDiscount } = useStaffDiscount(
    cart
  );
  const { user } = useAuth();
  const { credits } = useCredits();
  const { loyalty, showLoyalty: isShowLoyalty } = useLoyalty();
  const { config } = useLoyaltyContext();
  const market = process.env.GATSBY_VM_MARKET || "AU";
  const showLoyalty =
    isShowLoyalty &&
    configData[market].ALLOWED_CUSTOMER_GROUP_LOYALTY?.includes(
      user?.customer_group?.name?.toLocaleUpperCase()
    );

  const attainPoints: TierPoints = {
    green: config?.loyaltyTierRangeValues?.SILVER_LOW_VALUE, // SILVER
    silver: config?.loyaltyTierRangeValues?.GOLD_LOW_VALUE, // GOLD
    gold: config?.loyaltyTierRangeValues?.BLACK_LOW_VALUE, // BLACK
    black: config?.loyaltyTierRangeValues?.FOUNDERS_LOW_VALUE, // FOUNDER
  };

  const tierToAttain: TierToAttain = {
    green: [0, "NONE"], // [index, tierBefore]
    silver: [1, "GREEN"],
    gold: [2, "SILVER"],
    black: [3, "GOLD"],
  };

  const pointsMultiplier: number =
    config?.pointsMultiplier?.[loyalty?.loyalty_tier] || 1;

  const hideChatLogo = (hide: boolean) => {
    const chatLogo = document.getElementById(
      "hubspot-messages-iframe-container"
    );
    if (isBrowser() && chatLogo) {
      if (window.innerWidth < 768) {
        hide
          ? chatLogo.style.setProperty("z-index", "-1", "important")
          : chatLogo.style.removeProperty("z-index");
      }
    }
  };

  useEffect(() => {
    if (cart) {
      fetchTotalStaffDiscount(cart.lineItems);
    }
  }, [cart?.lineItems]);

  useEffect(() => {
    if (isCartOpen && cart?.cartQuantity > 0) {
      hideChatLogo(true);
    } else {
      hideChatLogo(false);
    }
  }, [isCartOpen, cart?.cartQuantity]);

  const ALLOW_ONE_CLICK_CHECKOUT =
    process.env.GATSBY_ALLOW_ONE_CLICK_CHECKOUT === "true";

  const banners: TierBanner = {
    green: greenBanner,
    silver: silverBanner,
    gold: goldBanner,
    black: platinumBanner,
  };

  const totalWinePrice = cart?.lineItems.reduce(
    (acc, item) => {
      if (item.skuType !== "NonWineSKU") {
        acc.totalPrice += item.extendedSalePrice;
      }
      return acc;
    },
    { totalPrice: 0 }
  );

  let { tier: tierKey } = determineTierIndex(
    user as VinoUser,
    showLoyalty,
    loyalty?.loyalty_tier
  );
  const totalAmount =
    (totalWinePrice?.totalPrice || 0) + (cart?.shippingTotal || 0);
  const _pointsToEarn: number = totalWinePrice?.totalPrice
    ? (totalAmount >= credits ? totalAmount - credits : 0) * pointsMultiplier
    : 0;

  let _currentPoints: number = loyalty?.loyalty_current_points || 0;
  const currentPoints = formatNumber(_currentPoints);
  const pointsToEarn = formatNumber(_pointsToEarn);
  const mockTotalPointsToEarn = formatNumber(+_currentPoints + +_pointsToEarn);

  const attainTieMatched = useMemo(() => {
    if (!config?.loyaltyTierRangeValues) return null;

    const loyaltyTierRangeValues = config.loyaltyTierRangeValues;
    const totalPointsNumber = parseInt(
      mockTotalPointsToEarn.replace(/,/g, ""),
      10
    );

    const tierNames = Object.keys(loyaltyTierRangeValues)
      .filter((key) => key.includes("_LOW_VALUE"))
      .map((key) => key.replace("_LOW_VALUE", ""))
      .sort(
        (a, b) =>
          loyaltyTierRangeValues[`${a}_LOW_VALUE`] -
          loyaltyTierRangeValues[`${b}_LOW_VALUE`]
      );

    const currentTier = tierNames.find((tier) => {
      const low = loyaltyTierRangeValues[`${tier}_LOW_VALUE`];
      const high = loyaltyTierRangeValues[`${tier}_HIGH_VALUE`];
      return totalPointsNumber >= low && totalPointsNumber < high;
    });

    let nextTier = currentTier;
    if (currentTier) {
      const currentIndex = tierNames.indexOf(currentTier);
      if (currentIndex !== -1 && currentIndex < tierNames.length - 1) {
        nextTier = tierNames[currentIndex + 1];
      }
    }

    return nextTier?.toLowerCase() || "None";
  }, [config, mockTotalPointsToEarn]);

  let sliderLocation = 0;
  const nextTier =
    tierToAttain[loyalty?.loyalty_tier?.toLowerCase() || "green"];
  const achievedTier =
    tierToAttain[attainTieMatched?.toLowerCase() || "silver"];
  const currentAchivedRange =
    attainPoints[achievedTier?.[1].toString().toLowerCase() || "green"];
  const totalPointsAfterPurchase =
    parseFloat(_currentPoints) + parseFloat(_pointsToEarn);
  sliderLocation = (totalPointsAfterPurchase / currentAchivedRange) * 100;
  sliderLocation = sliderLocation > 95 ? 95 : sliderLocation;
  const arrowPosition = sliderLocation < 20 ? 20 : sliderLocation;

  let fillStyle: any = {};
  let arrowStyle: any = {};
  let currentPointsStyle: any = {
    left: sliderLocation <= 50 ? -10 : -40,
    top: -23,
  };
  let toEarnStyle: any = { left: sliderLocation <= 40 ? 8 : 16, bottom: -23 };
  const overlayEarning = sliderLocation >= 95;

  fillStyle = {
    ...fillStyle,
    width: `${sliderLocation}%`,
    minWidth: 55,
  };
  arrowStyle.left = `${arrowPosition}%`;

  if (overlayEarning) {
    currentPointsStyle.display = "none";
    toEarnStyle.left = -80;
    toEarnStyle.bottom = 3;
  }

  let newTierKey = tierKey;
  const multipleTierAchieved: boolean =
    (nextTier?.[0] as number) + 1 < (achievedTier?.[0] as number);
  if (multipleTierAchieved)
    newTierKey = achievedTier?.[1]
      .toString()
      .toLowerCase() as LoyaltyTierLowerCase;

  return cart && cart.cartQuantity > 0 ? (
    <StyledWrapper width="100%">
      <Box position="relative" height={57} overflow="hidden">
        {user && showLoyalty && (
          <img
            src={
              banners[
                tierKey === "none"
                  ? (loyaltyTierNames.GREEN.toLocaleLowerCase() as string)
                  : (tierKey as string)
              ]
            }
            width={"100%"}
            style={{ position: "absolute", left: 0, top: 0, zIndex: 0 }}
          />
        )}
        <Box
          display={["flex", "flex"]}
          backgroundColor={user && showLoyalty ? "transparent" : "almostWhite"}
          borderBottom={user && showLoyalty ? 0 : "1px solid"}
          borderColor="divider"
          justifyContent="space-between"
          padding={3}
          zIndex={2}
          position="absolute"
          width="100%"
          onClick={() => onCloseRequested()}
        >
          <StyledHeading
            as="h3"
            color={user && showLoyalty ? "white" : "#131314"}
          >
            Your cart
          </StyledHeading>
          {showLoyalty && user && (
            <StyledTextPoints color="white">
              POINTS <strong>{currentPoints}</strong>
            </StyledTextPoints>
          )}
          <StyledButton>
            <Box display={["flex", "none"]}>
              <ArrowDownIcon
                color={user && showLoyalty ? "white" : "#131314"}
                size="1.4em"
              />
            </Box>
            <Box display={["none", "flex"]}>
              <ArrowUpIcon color={user && showLoyalty ? "white" : "#131314"} />
            </Box>
          </StyledButton>
        </Box>
      </Box>
      <StyledItemsWrapper
        width="100%"
        maxHeight={["220px", "30vh"]}
        overflow="auto"
      >
        {cart?.lineItems?.map((cartItem, i) => {
          const returnValue =
            cartItemsRegularPrice?.[cartItem.productId + "-" + cartItem.name]
              ?.totalRegularPrice;
          cartItem.extendedSalePrice = Number.isNaN(returnValue)
            ? 0
            : returnValue;
          return <CartItemRow key={i} cartItem={cartItem} />;
        })}
      </StyledItemsWrapper>

      <CartSubtotal py="18px" />
      <CartStoreCredit />
      <CartShipping />
      <CartPromotions inMiniCart />
      <CartTotal inMiniCart />
      <CartActions onCloseRequested={onCloseRequested} />
      {user && showLoyalty && (
        <Box px={20}>
          <Box
            style={{
              position: "relative",
            }}
            px={20}
            height={60}
            mt={15}
          >
            <Box
              height={60}
              mt={15}
              position={"relative"}
              marginLeft={38}
              marginRight={34}
            >
              <Box
                style={{
                  ...fillStyle,
                  height: 24,
                  top: 8,
                  background: `${COLORS[tierColors[newTierKey]]}43`,
                  position: "absolute",
                }}
              >
                <Box
                  style={{
                    float: "right",
                    height: 24,
                    width: 52,
                    background: "white",
                    position: "relative",
                  }}
                >
                  <Arrow
                    style={{
                      color: COLORS[tierColors[newTierKey]],
                      height: 24,
                    }}
                  />
                  <Box
                    position="absolute"
                    width="80px"
                    style={currentPointsStyle}
                  >
                    <Text
                      fontSize={"12px"}
                      textAlign="center"
                      fontWeight={500}
                      color="black"
                    >
                      {currentPoints} points
                    </Text>
                  </Box>
                  <Box position="absolute" width="80px" style={toEarnStyle}>
                    <Text
                      fontSize={"12px"}
                      textAlign="center"
                      color="black"
                      fontWeight={500}
                    >
                      {mockTotalPointsToEarn} points
                    </Text>
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box
              style={{
                left: 52,
                right: 52,
                height: 26,
                background: "transparent",
                position: "absolute",
                top: 7,
                borderTop: `1px solid ${COLORS[tierColors[newTierKey]]}`,
                borderBottom: `1px solid ${COLORS[tierColors[newTierKey]]}`,
                opacity: 0.25,
              }}
            />
            <Box
              style={{
                height: 40,
                width: 40,
                position: "absolute",
                left: 20,
                top: 0,
                border: "2px solid white",
                borderRadius: "100px",
                borderTopRightRadius: 0,
              }}
            >
              {rewardIconsV2[newTierKey]
                ? React.cloneElement(rewardIconsV2[newTierKey], {
                    size: "40px",
                  })
                : null}
            </Box>
            <Box
              style={{
                height: 40,
                width: 40,
                position: "absolute",
                right: 20,
                top: 0,
                border: "2px solid white",
                borderRadius: "100px",
                borderTopRightRadius: 0,
              }}
            >
              {rewardIconsV2[attainTieMatched]
                ? React.cloneElement(rewardIconsV2[attainTieMatched], {
                    size: "40px",
                  })
                : null}
            </Box>
          </Box>
          <Box>
            {multipleTierAchieved ? (
              <Text fontSize={"12px"} textAlign="center" color="black">
                YOU WILL EARN <strong>{pointsToEarn} POINTS</strong> AND ATTAIN{" "}
                <strong>{achievedTier[1]}</strong>
              </Text>
            ) : (
              <Text fontSize={"12px"} textAlign="center" color="black">
                YOU WILL EARN <strong>{pointsToEarn} POINTS</strong> ON THIS
                PURCHASE
              </Text>
            )}
          </Box>
        </Box>
      )}

      <Box
        width="100%"
        p={3}
        pt={0}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        {ALLOW_ONE_CLICK_CHECKOUT && user && cart.showOneClickCheckout && (
          <OneClickCheckout user={user} cart={cart} cartId={cart?.id} />
        )}
      </Box>
    </StyledWrapper>
  ) : (
    <Box padding={3} maxHeight={["220px", "30vh"]}>
      <Heading as="h3" variant="xxs" textAlign="center">
        Your cart is empty, add wines!
      </Heading>
    </Box>
  );
};
